import { type AskPunttMessage } from "@mg/schemas/src/christo/askPuntt";
import { ArrowUp, CaretRight } from "@phosphor-icons/react";
import { Button, Flex, Text, TextArea } from "@radix-ui/themes";
import { Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import { askPunttChatRoute } from "./chatRoute";
import { ChatMessage } from "./components/ChatMessage";
import { InputPlusMenu } from "./components/InputPlusMenu";
import { useAttachedFiles } from "./hooks/useAttachedFiles";
import { useStreamSimulation } from "./hooks/useStreamSimulation";
import { askPunttRoute } from "./route";

import { errorAnalyticsPayload, useAnalytics } from "../../utils/analytics";
import {
  useAskPunttChatHistory,
  useSendAskPunttMessage,
} from "../../utils/queries/askPuntt";

const CONNECTORS = [
  { id: "sharepoint", defaultEnabled: true },
  { id: "wrike", defaultEnabled: true },
  { id: "teams", defaultEnabled: false },
] as const;

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 120_000;

/** Convert chat's enabledConnectors array to Record for display */
function connectorsArrayToRecord(
  ids: string[] | undefined,
): Record<string, boolean> {
  const record: Record<string, boolean> = {};
  CONNECTORS.forEach((c) => {
    record[c.id] = ids?.includes(c.id) ?? c.defaultEnabled;
  });
  return record;
}

export default function AskPunttChat() {
  const { chatId } = askPunttChatRoute.useParams();
  const [waitingForAI, setWaitingForAI] = useState(false);

  const {
    data: chatData,
    isLoading,
    dataUpdatedAt,
  } = useAskPunttChatHistory(chatId, {
    refetchInterval: waitingForAI ? POLL_INTERVAL_MS : false,
  });
  const sendMessage = useSendAskPunttMessage(chatId);
  const analytics = useAnalytics("ask_puntt_chat");

  const [inputMessage, setInputMessage] = useState("");
  const [localMessages, setLocalMessages] = useState<AskPunttMessage[]>([]);
  const {
    attachedItems,
    addFiles,
    addFile,
    removeFile,
    clearAll,
    readyAttachments,
    hasPending,
  } = useAttachedFiles({
    onUploadError: (err) =>
      analytics.capture("ask_puntt_upload_error", {
        chat_id: chatId,
        ...errorAnalyticsPayload(err),
      }),
  });
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(
    null,
  );
  const [streamingMessage, setStreamingMessage] =
    useState<AskPunttMessage | null>(null);
  const [skipSearchPhase, setSkipSearchPhase] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const initialLoadRef = useRef(true);
  const knownMessageCountRef = useRef(0);
  const waitingSinceRef = useRef<number | null>(null);
  const dataAtWaitStartRef = useRef<number | null>(null);

  // Stream simulation for the latest assistant message
  const streamState = useStreamSimulation(
    streamingMessage,
    !!streamingMessage,
    {
      skipSearchPhase,
    },
  );

  // When streaming completes, clear the streaming state
  useEffect(() => {
    if (streamState.isComplete && streamingMessageId) {
      setStreamingMessageId(null);
      setStreamingMessage(null);
      setSkipSearchPhase(false);
    }
  }, [streamState.isComplete, streamingMessageId]);

  useEffect(() => {
    if (waitingForAI) {
      waitingSinceRef.current = Date.now();
    } else {
      waitingSinceRef.current = null;
      dataAtWaitStartRef.current = null;
    }
  }, [waitingForAI]);

  // Sync server messages with local state and detect completed assistant
  // responses.  We use `dataUpdatedAt` (changes on every successful refetch)
  // instead of `chatData?.messages` alone — TanStack Query's structural
  // sharing can return the same array reference when the deep content hasn't
  // changed from its perspective, which would prevent the effect from firing.
  useEffect(() => {
    if (!chatData?.messages) return;

    if (initialLoadRef.current) {
      initialLoadRef.current = false;
      const messages = chatData.messages;
      knownMessageCountRef.current = messages.length;
      setLocalMessages(messages);

      const last = messages[messages.length - 1];
      if (
        messages.length > 0 &&
        (last.role === "user" || (last.role === "assistant" && !last.content))
      ) {
        setWaitingForAI(true);
      }
      return;
    }

    setLocalMessages(chatData.messages);
    knownMessageCountRef.current = chatData.messages.length;

    if (!waitingForAI) return;

    if (
      waitingSinceRef.current &&
      Date.now() - waitingSinceRef.current > POLL_TIMEOUT_MS
    ) {
      setWaitingForAI(false);
      return;
    }

    // Skip response detection until we get fresh data after waiting started.
    // Without this, the effect fires with stale chatData (still showing the
    // previous assistant response) and immediately stops polling.
    if (
      dataAtWaitStartRef.current !== null &&
      dataUpdatedAt <= dataAtWaitStartRef.current
    ) {
      return;
    }

    const lastMsg = chatData.messages[chatData.messages.length - 1];
    if (lastMsg?.role === "assistant" && lastMsg.content) {
      setWaitingForAI(false);
      dataAtWaitStartRef.current = null;
      setSkipSearchPhase((lastMsg.searchProgress?.length ?? 0) > 0);
      setStreamingMessageId(lastMsg._id);
      setStreamingMessage(lastMsg);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUpdatedAt, waitingForAI]);

  useEffect(() => {
    analytics.capture("ask_puntt_chat_viewed", { chat_id: chatId });
  }, [chatId]);

  // Auto-scroll to bottom when messages or stream state changes
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [localMessages, streamState, waitingForAI]);

  const handleSend = async () => {
    if (!inputMessage.trim() || sendMessage.isPending || hasPending) return;

    const messageToSend = inputMessage;
    const attachments = [...readyAttachments];
    const messageLength = messageToSend.length;
    const messageIndex = localMessages.filter((m) => m.role === "user").length;

    const userMsg: AskPunttMessage = {
      _id: `local-user-${Date.now()}`,
      chatId,
      role: "user",
      content: messageToSend,
      attachments: attachments.length > 0 ? attachments : undefined,
      createdAt: new Date().toISOString(),
    };

    setLocalMessages((prev) => [...prev, userMsg]);
    setInputMessage("");
    clearAll();

    try {
      const result = await sendMessage.mutateAsync({
        message: messageToSend,
        attachments: attachments.length > 0 ? attachments : undefined,
      });

      // Replace the optimistic user message with the server-confirmed one
      setLocalMessages((prev) => {
        const withoutOptimistic = prev.filter((m) => m._id !== userMsg._id);
        return [...withoutOptimistic, result.userMessage];
      });
      knownMessageCountRef.current = localMessages.length;
      dataAtWaitStartRef.current = dataUpdatedAt;
      setWaitingForAI(true);

      const attachmentIds = attachments.map((att) => att.key);
      const chatConnectors = chatData?.chat?.enabledConnectors ?? [];

      analytics.capture("ask_puntt_message_sent", {
        chat_id: chatId,
        message_length: messageLength,
        has_attachments: attachments.length > 0,
        attachment_count: attachments.length,
        attachment_ids: attachmentIds,
        enabled_connectors: chatConnectors,
        message_index: messageIndex,
        workflow_id: result.workflowId,
      });
    } catch (error) {
      setLocalMessages((prev) => prev.filter((m) => m._id !== userMsg._id));

      const attachmentIds = attachments.map((att) => att.key);
      const chatConnectors = chatData?.chat?.enabledConnectors ?? [];

      analytics.capture("ask_puntt_message_send_error", {
        chat_id: chatId,
        has_attachments: attachments.length > 0,
        attachment_count: attachments.length,
        attachment_ids: attachmentIds,
        enabled_connectors: chatConnectors,
        ...errorAnalyticsPayload(error),
      });
      console.error("Failed to send message:", error);
    }
  };

  const chat = chatData?.chat;

  return (
    <div
      className="flex h-full flex-col overflow-hidden bg-puntt-neutral-gray-2"
      data-testid="ask-puntt-chat"
    >
      {/* Breadcrumb */}
      <Flex
        align="center"
        gap="1"
        className="shrink-0 border-b border-puntt-neutral-gray-5 bg-base-white p-4"
      >
        <Link to={askPunttRoute.to} className="puntt-link">
          <Text
            size="2"
            weight="medium"
            className="text-puntt-accent-11 hover:underline"
          >
            Ask Puntt
          </Text>
        </Link>
        {chat?.title && (
          <>
            <CaretRight size={12} className="text-puntt-neutral-gray-9" />
            <Text
              size="2"
              weight="medium"
              className="max-w-[400px] truncate text-puntt-neutral-gray-12"
            >
              {chat.title}
            </Text>
          </>
        )}
      </Flex>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-6">
        <div className="mx-auto max-w-[720px] space-y-4">
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-16 animate-pulse rounded-lg bg-puntt-neutral-gray-4"
                />
              ))}
            </div>
          ) : (
            <>
              {localMessages.map((message) => (
                <ChatMessage
                  key={message._id}
                  message={message}
                  streamState={
                    message._id === streamingMessageId ? streamState : undefined
                  }
                />
              ))}
              {waitingForAI &&
                !streamingMessageId &&
                localMessages[localMessages.length - 1]?.role !==
                  "assistant" && (
                  <div className="flex items-center gap-2 rounded-lg bg-base-white p-4">
                    <div className="flex gap-1">
                      <span className="size-2 animate-bounce rounded-full bg-puntt-accent-9 [animation-delay:0ms]" />
                      <span className="size-2 animate-bounce rounded-full bg-puntt-accent-9 [animation-delay:150ms]" />
                      <span className="size-2 animate-bounce rounded-full bg-puntt-accent-9 [animation-delay:300ms]" />
                    </div>
                    <Text size="2" className="text-puntt-neutral-gray-9">
                      Puntt is thinking...
                    </Text>
                  </div>
                )}
            </>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Area */}
      <div className="shrink-0 bg-puntt-neutral-gray-2 px-4 py-3">
        <div className="mx-auto max-w-[720px]">
          <div className="relative rounded-lg border border-puntt-neutral-gray-6 bg-base-white">
            <TextArea
              data-testid="ask-puntt-chat-reply-input"
              size="2"
              variant="surface"
              placeholder="Reply..."
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              className="min-h-[60px] w-full resize-none border-none !shadow-none"
              style={{ outline: "none", boxShadow: "none" }}
            />

            {/* Attached files preview */}
            {attachedItems.length > 0 && (
              <Flex gap="2" wrap="wrap" className="px-3 pb-1">
                {attachedItems.map((item, idx) => (
                  <Flex
                    key={item.id}
                    align="center"
                    gap="1"
                    className="rounded-md bg-puntt-neutral-gray-3 px-2 py-1"
                  >
                    <Text
                      size="1"
                      className="max-w-[150px] truncate text-puntt-neutral-gray-11"
                    >
                      {item.file.name}
                      {item.status === "pending" && " (uploading…)"}
                      {item.status === "error" && " (failed)"}
                    </Text>
                    <button
                      onClick={() => removeFile(idx)}
                      className="text-puntt-neutral-gray-9 hover:text-puntt-neutral-gray-12"
                    >
                      ×
                    </button>
                  </Flex>
                ))}
              </Flex>
            )}

            <Flex justify="between" align="center" className="px-3 py-1.5">
              <InputPlusMenu
                onFilesSelected={addFiles}
                onScreenshotCaptured={addFile}
                enabledConnectors={connectorsArrayToRecord(
                  chat?.enabledConnectors,
                )}
                connectorsDisabled
              />

              <Button
                data-testid="ask-puntt-chat-send"
                size="2"
                variant="ghost"
                disabled={
                  !inputMessage.trim() ||
                  sendMessage.isPending ||
                  hasPending ||
                  !!streamingMessageId ||
                  waitingForAI
                }
                onClick={handleSend}
                className="cursor-pointer"
              >
                <ArrowUp size={14} />
              </Button>
            </Flex>
          </div>
        </div>
      </div>
    </div>
  );
}
